import { Component, Inject, OnDestroy, ViewChild } from '@angular/core'
import { AuthTokenService } from '@helios/auth'
import { ResizeHandleState } from '@helios/generic-components'
import {
  EventType,
  GlobalEvent,
  GlobalMessagingService,
  SessionMessagingService,
} from '@helios/messaging'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import { ComponentContainer } from 'golden-layout'
import { merge, Observable, Subject } from 'rxjs'
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators'
import { DevToolsHttpComponent } from './components/dev-tools-http/dev-tools-http.component'
import {
  Devtools,
  DevtoolsGridComponent,
} from './components/devtools-grid/devtools-grid.component'
import { HttpRecorderService } from './services/http-recorder/http-recorder.service'

interface DevtoolsPanelState {
  resizeHandleState: ResizeHandleState
}

export type MessageType = 'Session' | 'Global'

@Component({
  selector: 'hs-devtools',
  templateUrl: './devtools-panel.component.html',
  styleUrls: ['./devtools-panel.component.scss'],
})
export class DevtoolsPanelComponent
  extends BasePanelComponent<DevtoolsPanelState>
  implements OnDestroy
{
  /**
   * @internal for testing
   */
  @ViewChild('devToolsSSE')
  readonly devToolsSSE!: DevtoolsGridComponent
  /**
   * @internal for testing
   */
  @ViewChild('devToolsHttp')
  readonly devToolsHttp!: DevToolsHttpComponent

  isPlaying = false
  clearHttpSubject = new Subject<void>()

  private readonly destroySubject$ = new Subject<void>()

  token$ = this.authTokenService.getToken()

  eventStream$: Observable<Devtools> = merge(
    this.sessionMessagingService.on<unknown, Devtools>(EventType.MESSAGE),
    this.globalMessagingService.on<unknown, Devtools>(EventType.MESSAGE),
  ).pipe(
    takeUntil(this.destroySubject$),
    filter(x => x != null),
    map(message => message as GlobalEvent<unknown>),
    map(message => {
      const sseType: MessageType =
        message.heliosSessionId != null ? 'Session' : 'Global'
      return {
        ...message,
        sseType,
        namespace: message.eventType?.split('/')[0] ?? 'Global',
      }
    }),
    filter(() => this.isPlaying),
  )

  recordedHttpRequests$ = this.httpRecorder.recordedHttpRequests$.pipe(
    filter(() => this.isPlaying),
  )

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly sessionMessagingService: SessionMessagingService,
    private readonly globalMessagingService: GlobalMessagingService,
    private readonly httpRecorder: HttpRecorderService,
    private readonly authTokenService: AuthTokenService,
  ) {
    super(container)
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  onPlay(isPlaying: boolean) {
    this.isPlaying = isPlaying
  }

  onClearData() {
    this.clearHttpSubject.next()
  }

  onResizeFinished(resizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      resizeHandleState,
    })
  }
}
